import { useEffect, useState, type ReactNode } from 'react';
import { api, ApiError, getApiToken, setApiToken } from '../lib/api.js';
import { BrandLogo } from './BrandLogo.js';

/**
 * Schützt die gesamte Admin-UI mit einem optionalen Passwort-Login.
 *
 * Ablauf:
 *  1. Beim Start: GET /api/v1/auth/status → `authRequired`?
 *  2. Wenn nein → Kinder rendern.
 *  3. Wenn ja → vorhandenen Token testen (GET /api/v1/config); 401 → Login-Form.
 *  4. Login-Form: POST /api/v1/auth/login {password} → speichert API_TOKEN
 *     in localStorage und lädt die Seite neu, damit alle Socket-Verbindungen
 *     mit dem neuen Token aufgebaut werden.
 *
 * Wenn ADMIN_PASSWORD und API_TOKEN beide leer sind (Dev-Standard), wird
 * der Login einfach übersprungen.
 */
export function LoginGate({ children }: { children: ReactNode }): JSX.Element {
  type Phase = 'checking' | 'open' | 'locked' | 'authed';
  const [phase, setPhase] = useState<Phase>('checking');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const status = await api.get<{ authRequired: boolean }>('/api/v1/auth/status');
        if (cancelled) return;
        if (!status.authRequired) {
          setPhase('open');
          return;
        }
        // Auth nötig — prüfen ob gespeicherter Token noch gültig ist.
        if (!getApiToken()) {
          setPhase('locked');
          return;
        }
        try {
          await api.get('/api/v1/config');
          setPhase('authed');
        } catch (err) {
          if (err instanceof ApiError && err.status === 401) {
            setApiToken('');
            setPhase('locked');
          } else {
            // anderer Fehler (Netzwerk etc.) — wir lassen das App-eigene Fehler-Handling rendern
            setPhase('authed');
          }
        }
      } catch (err) {
        // /auth/status erreichbar nicht → wir lassen die App rendern, damit ein
        // Verbindungsfehler dort sichtbar wird (statt einer Endlos-Sperre).
        console.warn('[login] status check failed', err);
        if (!cancelled) setPhase('open');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.post<{ token: string }>('/api/v1/auth/login', { password });
      setApiToken(res.token);
      // Reload, damit Socket.io + Stores frisch mit dem neuen Token starten.
      window.location.reload();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) setError('Zu viele Versuche – kurz warten.');
        else if (err.status === 401) setError('Falsches Passwort.');
        else setError(err.message);
      } else {
        setError('Login fehlgeschlagen.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (phase === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-sm text-text-mute">Lade …</div>
      </div>
    );
  }

  if (phase === 'locked') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg p-6">
        <form
          onSubmit={(e) => void handleSubmit(e)}
          className="card w-full max-w-sm p-6 space-y-4"
        >
          <div className="space-y-1">
            <BrandLogo size="lg" />
            <p className="text-sm text-text-mute pt-1">
              Intercom · Admin-Anmeldung erforderlich.
            </p>
          </div>
          <label className="block space-y-1">
            <span className="text-xs text-text-mute">Passwort</span>
            <input
              type="password"
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input w-full"
            />
          </label>
          {error && <div className="text-sm text-red-400">{error}</div>}
          <button
            type="submit"
            disabled={submitting || password.length === 0}
            className="w-full px-3 py-2 rounded bg-accent text-bg text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? 'Anmelden …' : 'Anmelden'}
          </button>
        </form>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Hilfsfunktion für einen Logout-Button (siehe Sidebar/SettingsPage).
 */
export function logout(): void {
  setApiToken('');
  window.location.reload();
}
